import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaelligeVertragsrechnung, Vertrag } from '../types';
import { useRechte } from '../hooks/useRechte';
import { darfBelegeSenden, hatRecht } from '../lib/rechte';
import { euro } from '../lib/vertrag';
import { FESTSCHREIBEN_FRAGE, fehlerText } from '../lib/belegKarte';
import VertragAkte, { VertragStatusChip, chipBase } from './VertragAkte';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// ── helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE');
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

// ── chips ──────────────────────────────────────────────────────────────────

const TYP_CHIP: Record<string, React.CSSProperties> = {
  EINKAUF: { background: 'var(--bone-2)', color: 'var(--green-900)', border: '1px solid var(--green-400)' },
  VERKAUF: { background: 'var(--bone-2)', color: 'var(--green-500)', border: '1px solid var(--green-400)' },
};

// ── VertragForm ────────────────────────────────────────────────────────────

interface VertragFormProps {
  onSaved: () => void;
  onCancel: () => void;
}

export const VertragForm: React.FC<VertragFormProps> = ({ onSaved, onCancel }) => {
  const [form, setForm] = useState({
    typ: 'EINKAUF',
    titel: '',
    beschreibung: '',
    partner_id: '',
    laufzeit_start: '',
    laufzeit_ende: '',
    laufzeit_monate: '',
    monatlicher_betrag: '',
    betrag_gesamt: '',
    kuendigungsfrist_tage: '30',
    auto_verlaengerung: false,
    status: 'ENTWURF',
    notizen: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/vertraege`, {
        typ: form.typ,
        titel: form.titel,
        beschreibung: form.beschreibung || null,
        partner_id: form.partner_id ? parseInt(form.partner_id) : null,
        laufzeit_start: form.laufzeit_start || null,
        laufzeit_ende: form.laufzeit_ende || null,
        laufzeit_monate: form.laufzeit_monate ? parseInt(form.laufzeit_monate) : null,
        monatlicher_betrag: form.monatlicher_betrag ? parseFloat(form.monatlicher_betrag) : null,
        betrag_gesamt: form.betrag_gesamt ? parseFloat(form.betrag_gesamt) : null,
        kuendigungsfrist_tage: parseInt(form.kuendigungsfrist_tage),
        auto_verlaengerung: form.auto_verlaengerung,
        status: form.status,
        notizen: form.notizen || null,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const set = (field: string, value: string | boolean) =>
    setForm(f => ({ ...f, [field]: value }));

  return (
    <form onSubmit={handleSubmit} style={{ background: 'var(--bone-2)', padding: 16, borderRadius: 0, marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <input
          required
          placeholder="Titel *"
          value={form.titel}
          onChange={e => set('titel', e.target.value)}
          style={{ padding: 8 }}
        />
        <select value={form.typ} onChange={e => set('typ', e.target.value)} style={{ padding: 8 }}>
          <option value="EINKAUF">Einkauf</option>
          <option value="VERKAUF">Verkauf</option>
        </select>
        <select value={form.status} onChange={e => set('status', e.target.value)} style={{ padding: 8 }}>
          <option value="ENTWURF">Entwurf</option>
          <option value="AKTIV">Aktiv</option>
          <option value="GEKUENDIGT">Gekündigt</option>
          <option value="ABGELAUFEN">Abgelaufen</option>
        </select>
        <input
          placeholder="Partner-ID"
          type="number"
          value={form.partner_id}
          onChange={e => set('partner_id', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Laufzeit Start"
          type="date"
          value={form.laufzeit_start}
          onChange={e => set('laufzeit_start', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Laufzeit Ende"
          type="date"
          value={form.laufzeit_ende}
          onChange={e => set('laufzeit_ende', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Laufzeit (Monate)"
          type="number"
          value={form.laufzeit_monate}
          onChange={e => set('laufzeit_monate', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Monatlicher Betrag €"
          type="number"
          step="0.01"
          value={form.monatlicher_betrag}
          onChange={e => set('monatlicher_betrag', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Gesamtbetrag €"
          type="number"
          step="0.01"
          value={form.betrag_gesamt}
          onChange={e => set('betrag_gesamt', e.target.value)}
          style={{ padding: 8 }}
        />
        <input
          placeholder="Kündigungsfrist (Tage)"
          type="number"
          value={form.kuendigungsfrist_tage}
          onChange={e => set('kuendigungsfrist_tage', e.target.value)}
          style={{ padding: 8 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8 }}>
          <input
            type="checkbox"
            checked={form.auto_verlaengerung}
            onChange={e => set('auto_verlaengerung', e.target.checked)}
          />
          Auto-Verlängerung
        </label>
        <textarea
          placeholder="Beschreibung"
          value={form.beschreibung}
          onChange={e => set('beschreibung', e.target.value)}
          style={{ padding: 8, resize: 'vertical' }}
        />
        <textarea
          placeholder="Notizen"
          value={form.notizen}
          onChange={e => set('notizen', e.target.value)}
          style={{ padding: 8, resize: 'vertical' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="submit" className="k-btn k-btn--primary" disabled={saving}>
          {saving ? 'Speichert…' : 'Speichern'}
        </button>
        <button type="button" className="k-btn k-btn--ghost" onClick={onCancel}>
          Abbrechen
        </button>
      </div>
    </form>
  );
};

// ── Fällige Vertragsabrechnungen ───────────────────────────────────────────

/** Karte „Fällige Vertragsabrechnungen“ (Spec §6.3): Rechnungsentwürfe aus Verträgen,
 *  Einzel-Freigabe und „Alle freigeben“ (sequenziell, Abbruch beim ersten Fehler). */
const FaelligeKarte: React.FC<{
  eintraege: FaelligeVertragsrechnung[];
  onGeaendert: () => void;
  onOeffneVertrag: (vertragId: number) => void;
}> = ({ eintraege, onGeaendert, onOeffneVertrag }) => {
  const { rechte } = useRechte();
  const darfFreigeben = darfBelegeSenden(rechte);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const freigeben = async (liste: FaelligeVertragsrechnung[], frage: string) => {
    if (!window.confirm(frage)) return;
    setFehler(null);
    setLaeuft(true);
    let fertig = 0;
    try {
      for (const r of liste) {
        try {
          await axios.post(`${API_URL}/api/dokumente/ausgangsrechnungen/${r.ausgangsrechnung_id}/festschreiben`);
          fertig += 1;
        } catch (err: unknown) {
          const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
          const vorher = fertig > 0 ? ` (${fertig} davor freigegeben)` : '';
          setFehler(`${r.nummer_anzeige}: ${fehlerText(detail, 'Freigabe fehlgeschlagen.')}${vorher}`);
          break;
        }
      }
    } finally {
      setLaeuft(false);
      onGeaendert();
    }
  };

  const zelle: React.CSSProperties = { padding: '4px 8px', textAlign: 'left' };

  return (
    <div style={{ background: 'var(--bone-2)', border: '1px solid var(--bone-3)', padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>Fällige Vertragsabrechnungen ({eintraege.length})</h3>
        {darfFreigeben && (
          <button
            className="k-btn k-btn--primary k-btn--sm"
            style={{ marginLeft: 'auto' }}
            disabled={laeuft}
            onClick={() => freigeben(
              eintraege,
              `${eintraege.length} Rechnung(en) festschreiben? Sie erhalten ihre Nummer und können nicht mehr bearbeitet werden.`,
            )}
          >
            {laeuft ? 'Gibt frei…' : 'Alle freigeben'}
          </button>
        )}
      </div>
      {fehler && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 8 }}>{fehler}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={zelle}>Rechnung</th>
            <th style={zelle}>Vertrag</th>
            <th style={zelle}>Kunde</th>
            <th style={zelle}>Periode</th>
            <th style={zelle}>Version</th>
            <th style={{ ...zelle, textAlign: 'right' }}>Brutto</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {eintraege.map(r => (
            <tr key={r.ausgangsrechnung_id} style={{ borderTop: '1px solid var(--bone-3)' }}>
              <td style={zelle}>{r.nummer_anzeige}</td>
              <td style={zelle}>
                <button className="k-btn k-btn--ghost k-btn--sm" onClick={() => onOeffneVertrag(r.vertrag_id)}>
                  {r.vertrag_nr || `#${r.vertrag_id}`}
                </button>{' '}
                {r.titel}
              </td>
              <td style={zelle}>{r.partner_name || '—'}</td>
              <td style={zelle}>{formatDate(r.leistungszeitraum_von)} – {formatDate(r.leistungszeitraum_bis)}</td>
              <td style={zelle}>{r.version_label ?? '—'}</td>
              <td style={{ ...zelle, textAlign: 'right' }}>{euro(r.brutto_gesamt)}</td>
              <td style={{ ...zelle, textAlign: 'right' }}>
                {darfFreigeben && (
                  <button className="k-btn k-btn--ghost k-btn--sm" disabled={laeuft}
                          onClick={() => freigeben([r], FESTSCHREIBEN_FRAGE)}>
                    Freigeben
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── VertragList ────────────────────────────────────────────────────────────

interface VertragListProps {
  /** Vertragsakte direkt öffnen (Navigation aus Auftrags-/Partnerakte, Parameter `vertrag_id`). */
  initialAkteId?: number | null;
  onInitialAkteConsumed?: () => void;
}

const VertragList: React.FC<VertragListProps> = ({ initialAkteId, onInitialAkteConsumed }) => {
  const [vertraege, setVertraege] = useState<Vertrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [filterTyp, setFilterTyp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [akteId, setAkteId] = useState<number | null>(initialAkteId ?? null);
  const [faellige, setFaellige] = useState<FaelligeVertragsrechnung[]>([]);
  const [faelligeFehler, setFaelligeFehler] = useState<string | null>(null);
  const [aktionFehler, setAktionFehler] = useState<string | null>(null);
  const { rechte } = useRechte();
  const darfKuendigen = hatRecht(rechte, 'vertrag.loeschen');

  useEffect(() => {
    if (initialAkteId == null) return;
    setAkteId(initialAkteId);
    onInitialAkteConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAkteId]);

  const ladeFaellige = () => {
    axios
      .get<FaelligeVertragsrechnung[]>(`${API_URL}/api/vertraege/abrechnungen/faellig`)
      .then(r => { setFaellige(r.data); setFaelligeFehler(null); })
      .catch((err: unknown) => {
        const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
        setFaellige([]);
        setFaelligeFehler(`Fällige Vertragsabrechnungen konnten nicht geladen werden: ${fehlerText(detail, 'Unbekannter Fehler')}`);
      });
  };

  const load = () => {
    setLoading(true);
    setFehler(null);
    ladeFaellige();
    axios
      .get<Vertrag[]>(`${API_URL}/api/vertraege`, {
        params: {
          ...(filterTyp && { typ: filterTyp }),
          ...(filterStatus && { status: filterStatus }),
        },
      })
      .then(r => setVertraege(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Verträge konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filterTyp, filterStatus]);

  if (akteId != null) {
    return <VertragAkte vertragId={akteId} onClose={() => { setAkteId(null); load(); }} />;
  }

  const handleKuendigen = (v: Vertrag) => {
    if (!window.confirm(`Vertrag "${v.titel}" wirklich kündigen?`)) return;
    setAktionFehler(null);
    axios
      .delete(`${API_URL}/api/vertraege/${v.vertrag_id}`)
      .then(() => load())
      .catch((err: unknown) => {
        const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
        setAktionFehler(`„${v.titel}“ konnte nicht gekündigt werden: ${fehlerText(detail, 'Unbekannter Fehler')}`);
      });
  };

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Verträge</h2>
        <button
          className={`k-btn ${showForm ? 'k-btn--ghost' : 'k-btn--primary'}`}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Abbrechen' : '+ Neuer Vertrag'}
        </button>
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          value={filterTyp}
          onChange={e => setFilterTyp(e.target.value)}
          style={{ padding: '6px 10px' }}
        >
          <option value="">Alle Typen</option>
          <option value="EINKAUF">Einkauf</option>
          <option value="VERKAUF">Verkauf</option>
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '6px 10px' }}
        >
          <option value="">Alle Status</option>
          <option value="ENTWURF">Entwurf</option>
          <option value="AKTIV">Aktiv</option>
          <option value="GEKUENDIGT">Gekündigt</option>
          <option value="ABGELAUFEN">Abgelaufen</option>
        </select>
      </div>

      {faelligeFehler && (
        <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 16 }}>{faelligeFehler}</div>
      )}
      {aktionFehler && (
        <div role="alert" style={{ color: 'var(--red)', fontSize: 13, marginBottom: 16 }}>{aktionFehler}</div>
      )}
      {faellige.length > 0 && (
        <FaelligeKarte eintraege={faellige} onGeaendert={ladeFaellige} onOeffneVertrag={setAkteId} />
      )}

      {/* Form */}
      {showForm && (
        <VertragForm
          onSaved={() => { setShowForm(false); load(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Table */}
      {loading ? (
        <div style={{ padding: 16 }}>Lade Verträge…</div>
      ) : fehler ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: 'var(--bone-2)', border: '1px solid var(--bone-3)', borderRadius: 0,
          fontSize: 13, color: 'var(--red)',
        }}>
          <span>{fehler}</span>
          <button
            className="k-btn k-btn--danger k-btn--sm"
            onClick={load}
            style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}
          >
            Erneut versuchen
          </button>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bone-3)' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Titel</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Typ</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '8px 12px', textAlign: 'right' }}>Monatlich</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Laufzeit Ende</th>
              <th style={{ padding: '8px 12px', textAlign: 'left' }}>Nächstes Kündigung</th>
              <th style={{ padding: '8px 12px', textAlign: 'center' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {vertraege.map(v => {
              const kuendigungDays = daysUntil(v.naechstes_kuendigungsdatum);
              const kuendigungSoon = kuendigungDays !== null && kuendigungDays <= 7;
              return (
                <React.Fragment key={v.vertrag_id}>
                  <tr style={{ borderBottom: '1px solid var(--bone-3)' }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                      {v.vertrag_nr && <span style={{ color: 'var(--ink-2)', marginRight: 8 }}>{v.vertrag_nr}</span>}
                      {v.titel}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span style={{ ...chipBase, ...(TYP_CHIP[v.typ] ?? {}) }}>{v.typ}</span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <VertragStatusChip status={v.status} />
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {euro(v.monatlicher_betrag)}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{formatDate(v.laufzeit_ende)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {v.naechstes_kuendigungsdatum ? (
                        <span style={kuendigungSoon ? { color: 'var(--red)', fontWeight: 700 } : {}}>
                          {formatDate(v.naechstes_kuendigungsdatum)}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {v.status === 'AKTIV' && darfKuendigen && (
                        <button
                          className="k-btn k-btn--danger k-btn--sm"
                          onClick={() => handleKuendigen(v)}
                        >
                          Kündigen
                        </button>
                      )}{' '}
                      <button
                        className="k-btn k-btn--ghost k-btn--sm"
                        onClick={() => setAkteId(v.vertrag_id)}
                      >
                        Akte
                      </button>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
            {vertraege.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>
                  Keine Verträge vorhanden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default VertragList;
